import type { ReactNode } from 'react'
import { AttributionFooter } from '../about/AttributionFooter'
import { OverlayHost } from '../about/OverlayHost'
import { LANGUAGES, useLanguage, useStrings } from '../i18n'
import { isLanguage } from '../i18n/strings'
import { DataHealthBanner } from './DataHealthBanner'

/**
 * The shell.
 *
 * Three regions, in the order a keyboard should meet them: search and filters,
 * then the map, then the details. Each is a named landmark, so a screen-reader
 * rotor lists "Search and filters / Map / Details and legend" rather than three
 * anonymous regions, and the two skip links let somebody jump straight past the
 * chrome to either the search box or the canvas.
 *
 * Below the one breakpoint this stops being three columns: the map takes the
 * whole body and the two side regions become sheets anchored to the bottom of
 * the viewport, in thumb reach. That is done in CSS on the same DOM, so the tab
 * order does not change with the window width.
 */
export const AppLayout = ({
  filters,
  map,
  detail,
}: {
  filters: ReactNode
  map: ReactNode
  detail: ReactNode
}) => {
  const strings = useStrings()
  const { language, setLanguage } = useLanguage()

  return (
    <div className="layout">
      <a className="skip-link" href="#filters">
        {strings.app.skipToSearch}
      </a>
      <a className="skip-link" href="#map">
        {strings.app.skipToMap}
      </a>

      <header className="layout__header">
        <div className="layout__brand">
          <h1 className="layout__title">{strings.app.title}</h1>
          <p className="layout__tagline">{strings.app.tagline}</p>
        </div>

        <label className="layout__language">
          <span className="visually-hidden">{strings.app.languageLabel}</span>
          <select
            className="layout__language-select"
            value={language}
            onChange={(event) => {
              if (isLanguage(event.target.value)) setLanguage(event.target.value)
            }}
          >
            {LANGUAGES.map((code) => (
              <option key={code} value={code}>
                {code.toUpperCase()}
              </option>
            ))}
          </select>
        </label>
      </header>

      <DataHealthBanner />

      {/* Each panel owns its own landmark and its own accessible name - the
          shell only decides where the three sit relative to each other, so
          there is no second, redundant region wrapped around each one. */}
      <div className="layout__body">
        <div className="layout__region layout__region--filters" id="filters" data-slot="filters">
          {filters}
        </div>

        <main
          className="layout__region layout__region--map"
          id="map"
          data-slot="map"
          aria-label={strings.map.label}
          tabIndex={-1}
        >
          {map}
        </main>

        <div className="layout__region layout__region--detail" data-slot="detail">
          {detail}
        </div>
      </div>

      {/* Attribution is a licence obligation, so it is chrome rather than a
          region: it stays on screen whatever the three panels render. */}
      <footer className="layout__footer">
        <AttributionFooter />
      </footer>

      {/* The about page and the methodology modal, mounted as chrome: the
          modal is opened from a voltage badge inside the detail panel, so it
          must not depend on the footer being on screen. */}
      <OverlayHost />
    </div>
  )
}
